/**
 * Standard Location Provider Types
 *
 * All location providers must return data in this format.
 * This enables switching providers (Radar, Google, etc.) without changing downstream code.
 */

/**
 * @typedef {Object} Coordinates
 * @property {number} latitude
 * @property {number} longitude
 */

/**
 * @typedef {Object} Venue
 * @property {string|null} name - Venue/business name (e.g., "Starbucks", "Egyptian Theatre")
 * @property {string|null} category - Venue category (e.g., "coffee-shop", "theater")
 * @property {string|null} chain - Chain name if applicable (e.g., "Starbucks")
 */

/**
 * @typedef {Object} Address
 * @property {string|null} street - Street name without number (e.g., "2nd Ave")
 * @property {string|null} streetNumber - House/building number (e.g., "956")
 * @property {string|null} neighborhood - Neighborhood name (e.g., "Midtown East", "DUMBO")
 * @property {string|null} city - Full city name (e.g., "New York")
 * @property {string|null} cityAbbr - Abbreviated city (e.g., "NYC")
 * @property {string|null} state - Full state name (e.g., "New York")
 * @property {string|null} stateCode - State code (e.g., "NY")
 * @property {string|null} country - Full country name (e.g., "United States")
 * @property {string|null} countryCode - Country code (e.g., "US")
 * @property {string|null} postalCode - Postal/ZIP code (e.g., "10022")
 * @property {string} formattedAddress - Full formatted address string
 */

/**
 * @typedef {Object} StandardLocationResponse
 * @property {Coordinates} coordinates - Lat/lng coordinates
 * @property {Venue} venue - Currently selected venue/business (default: closest one)
 * @property {Venue[]} nearbyVenues - All nearby venues for dropdown selection
 * @property {Address} address - Address components
 * @property {'radar'|'google'} provider - Which provider returned this data
 * @property {'high'|'medium'|'low'} confidence - Confidence level of the result
 * @property {Object} raw - Original provider response (for debugging)
 */

/**
 * Create an empty/default location response
 * @param {Coordinates} coordinates - The coordinates to include
 * @param {'radar'|'google'} provider - Provider name
 * @returns {StandardLocationResponse}
 */
export function createEmptyResponse(coordinates, provider) {
  return {
    coordinates,
    venue: {
      name: null,
      category: null,
      chain: null,
    },
    nearbyVenues: [], // Empty array when no venues found
    address: {
      street: null,
      streetNumber: null,
      neighborhood: null,
      city: null,
      cityAbbr: null,
      state: null,
      stateCode: null,
      country: null,
      countryCode: null,
      postalCode: null,
      formattedAddress: '',
    },
    provider,
    confidence: 'low',
    raw: null,
  };
}

/**
 * City abbreviation map
 */
export const CITY_ABBREVIATIONS = {
  'new york': 'NYC',
  'new york city': 'NYC',
  brooklyn: 'Brooklyn', // Keep as-is, it's already short
  queens: 'Queens',
  'los angeles': 'LA',
  'san francisco': 'SF',
  chicago: 'Chi',
  philadelphia: 'Phila',
  washington: 'DC',
  'las vegas': 'LV',
  houston: 'HOU',
  denver: 'DEN',
  boston: 'BOS',
  'new orleans': 'NO',
  jacksonville: 'Jax',
  'san antonio': 'SA',
  detroit: 'Det',
  seattle: 'SEA',
  portland: 'PDX',
  miami: 'MIA',
  atlanta: 'ATL',
  'kansas city': 'KC',
  london: 'LDN',
  tokyo: 'TYO',
  'hong kong': 'HK',
  'mexico city': 'MEX',
  'rio de janeiro': 'Rio',
  singapore: 'SG',
};

/**
 * Get city abbreviation
 * @param {string} cityName
 * @returns {string}
 */
export function getCityAbbreviation(cityName) {
  if (!cityName) return null;
  const normalized = cityName.toLowerCase().trim();
  return CITY_ABBREVIATIONS[normalized] || cityName;
}

/**
 * US State abbreviation map
 */
export const STATE_ABBREVIATIONS = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
};

/**
 * Get state abbreviation
 * @param {string} stateName
 * @returns {string}
 */
export function getStateAbbreviation(stateName) {
  if (!stateName) return null;
  const normalized = stateName.toLowerCase().trim();
  return STATE_ABBREVIATIONS[normalized] || stateName;
}

/**
 * Abbreviate street suffixes
 * @param {string} street
 * @returns {string}
 */
export function abbreviateStreet(street) {
  if (!street) return null;
  return street
    .replace(/\bStreet\b/gi, 'St')
    .replace(/\bRoad\b/gi, 'Rd')
    .replace(/\bAvenue\b/gi, 'Ave')
    .replace(/\bBoulevard\b/gi, 'Blvd')
    .replace(/\bDrive\b/gi, 'Dr')
    .replace(/\bLane\b/gi, 'Ln')
    .replace(/\bCourt\b/gi, 'Ct')
    .replace(/\bPlace\b/gi, 'Pl');
}

/**
 * Abbreviate neighborhood names.
 * Currently only handles Paris arrondissements ("4e Arrondissement" → "4e Arr").
 * Radar doesn't return Paris neighborhood names (Le Marais, Montmartre, etc.) —
 * arrondissement numbers are the best available data.
 */
export function abbreviateNeighborhood(neighborhood) {
  if (!neighborhood) return null;
  return neighborhood.replace(/\bArrondissement\b/gi, 'Arr');
}
